import { useEffect, useId, useState } from 'react'

const inputClass =
  'h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-primary focus:border-2 focus:border-gray-500 focus:outline-none'

const labelClass = 'mb-1 block text-xs font-semibold text-sidebar-primary'

type TextProps = {
  label: string
  value: string
  onApply: (value: string) => void
  placeholder?: string
}

/**
 * Controlled text filter for the always-visible log filter bar. Holds its own
 * draft state while typing and applies (via `onApply`) on Enter or blur, so it
 * doesn't fire a request on every keystroke. Re-syncs to `value` when the URL
 * changes externally (e.g. the Clear button).
 */
export const FilterTextInput = ({
  label,
  value,
  onApply,
  placeholder,
}: TextProps) => {
  const inputId = useId()
  const [draft, setDraft] = useState(value)

  useEffect(() => {
    setDraft(value)
  }, [value])

  const apply = () => {
    const trimmed = draft.trim()
    if (trimmed !== value) {
      onApply(trimmed)
    }
  }

  return (
    <div className="min-w-[200px]">
      <label htmlFor={inputId} className={labelClass}>
        {label}
      </label>
      <input
        id={inputId}
        type="text"
        value={draft}
        placeholder={placeholder ?? `Search ${label.toLowerCase()}...`}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => event.key === 'Enter' && apply()}
        onBlur={apply}
        className={inputClass}
      />
    </div>
  )
}

type DateProps = {
  label: string
  value: string
  onChange: (value: string) => void
}

/**
 * Controlled date filter for the always-visible log filter bar. Applies
 * immediately on change.
 */
export const FilterDateInput = ({ label, value, onChange }: DateProps) => {
  const inputId = useId()

  return (
    <div className="min-w-[170px]">
      <label htmlFor={inputId} className={labelClass}>
        {label}
      </label>
      <input
        id={inputId}
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={inputClass}
      />
    </div>
  )
}
